import { useState, type FormEvent, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Caption1,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Tag,
  TagGroup,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import { candidateSourceLabels, candidateSourceSchema, type Interviewer, type Person } from '@vigeo/shared';
import { shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { changeAgencyInterviewers, createPerson, fetchAgencyInterviewers, fetchCatalogOptions, type ApiError } from '@/lib/api';

/**
 * The dropdown's one entry that is not a person (#372 part 4).
 *
 * A native `<select>` cannot hold a button, and Paul asked for "+ Add" in the dropdown itself, so
 * the control is the last option and `pickInterviewer` intercepts it before any lookup. Prefixed so
 * it can never collide with a server-minted interviewer id.
 */
const ADD_INTERVIEWER = '__add-interviewer__';

/** The words on the two list controls, kept together so the tests and the screen read the same. */
export const interviewerListCopy = {
  add: '+ Add an interviewer to the list…',
  newName: 'New interviewer’s name',
  save: 'Add to the list',
  cancel: 'Cancel',
  manage: 'Manage the list',
  done: 'Done managing the list',
  remove: (label: string) => `Remove ${label} from the list`,
  /** Paul's own assurance, said where the decision is made rather than in a release note. */
  removalIsNotDeletion: 'Removing a name here only takes it out of this dropdown. Interviews that already name them keep the name.',
  empty: 'Nobody is on the list yet. Add the first name.',
} as const;

const useStyles = makeStyles({
  /**
   * The frame's card body: full width, rows stacked with a rule between them. No `maxWidth` — the
   * card is the measure, and capping the form at 44rem was what squeezed the role chips into two
   * columns regardless of the window.
   */
  form: { display: 'flex', flexDirection: 'column', alignSelf: 'stretch', minWidth: 0 },
  /**
   * A ROW, NOT A STACK. Frame `289:295` onward: a 240px label column carrying the heading and its
   * description, then the fields to its right, with a rule separating each row from the next.
   * Stacking the label above the fields is what made this read as a different screen from the
   * design however correct the fields inside it were.
   */
  section: {
    display: 'flex',
    // Explicit, not inherited: this file's own `roleChips` comment records what happens when a
    // flex direction is left to a default.
    flexDirection: 'row',
    // 32px: the label column ends at x=260 in the frame and the fields start at x=292.
    gap: tokens.spacingHorizontalXXXL,
    minWidth: 0,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    [shellMedia.belowTablet]: { flexDirection: 'column', gap: tokens.spacingVerticalS },
  },
  labelColumn: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    width: '15rem', flexShrink: 0,
    [shellMedia.belowTablet]: { width: 'auto' },
  },
  sectionFields: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0, flexGrow: 1 },
  h: { margin: 0, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
  /**
   * A row of fields, sized as frame 289:152 sizes them: every field in a Line is `flex: 1 0 0`
   * with `min-width: 0`, so a row of three is three equal thirds. Without the child rule each
   * field sized to its own content, which is why Preferred name rendered narrower than First and
   * Last name and the row did not fill its container.
   */
  pair: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    // The frame's Line is `items-start`. Left to stretch, a column whose control is taller (the
    // interviewer adder, which wraps its buttons) dragged its neighbours' inputs out of line.
    alignItems: 'flex-start',
    /**
     * Equal columns on a wide row, stacked on a narrow one.
     *
     * `flex: 1 0 0` — which this was — gives equal thirds but makes `flexWrap` DEAD CODE: with a
     * zero basis every item always "fits", so the row never wraps. At 375px each column collapsed
     * to 93px while Fluent's `Input` keeps its own ~180px minimum, and the three inputs physically
     * OVERLAPPED, the last running past the right edge of the viewport.
     *
     * A real basis fixes both: the columns still grow to equal widths when there is room, and when
     * three of them no longer fit they wrap instead of overlapping. `min-width` is left at its
     * default so an item cannot shrink below its content.
     */
    '> *': { flex: '1 1 12rem' },
  },
  options: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
    // The frame's footer reads left to right: what happens next, then Cancel, then Add candidate.
    justifyContent: 'space-between',
    alignSelf: 'stretch',
  },
  actionButtons: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' },
  /**
   * CONTENT-SIZED CHIPS THAT WRAP, which is what frame `289:152` shows — not a uniform grid.
   *
   * The grid was built from a prose description while the Figma seat was exhausted. With the frame
   * open: "Registered Nurse (RN)" and "Certified Occupational Therapy Assistant (COTA)" are
   * different widths and sit two to four per row, so equal columns are the wrong shape and leave a
   * short label swimming in a wide cell.
   */
  roleChips: {
    display: 'flex',
    /*
     * ROW, EXPLICITLY. Fluent's RadioGroup root ships `flex-direction: column`, and Griffel merges
     * per property — so a className that sets display and flex-wrap but not direction leaves the
     * column in place, and `flex-wrap` on an unbounded column wraps nothing. The chips rendered as
     * a single 14-high stack: worse than the uniform grid this replaced, and the claim in the
     * commit that they sit "two to four per row" was false on screen.
     */
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    alignItems: 'flex-start',
  },
  roleTile: {
    display: 'flex',
    // Content width, not a stretched cell.
    flexGrow: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingHorizontalS,
    /**
     * THE INPUT IS THE TILE, which is what makes the comment below true rather than aspirational.
     *
     * Fluent sizes `.fui-Radio__input` to the indicator — 32px wide, and only as tall as the
     * root's padding box — and gives the label `align-self: center`, so the label covers 36px of
     * a 42px tile. Measured with `elementFromPoint` across the tile: the top three and bottom
     * three rows to the right of the indicator hit `span.fui-Radio`, which listens for nothing.
     * A tap there did nothing. Stretching the input over the whole padding box makes every
     * pixel of the tile select the role, at every width — the input is `opacity: 0`, so this
     * changes what the tile responds to and not how it looks.
     */
    '& .fui-Radio__input': { width: '100%', height: '100%' },
    /**
     * AND ON A PHONE THAT TILE IS THE 44px TARGET, where left to itself it measured 42.
     *
     * A single-line tile is border 1 + padding 4 + content 32 + padding 4 + border 1. The content
     * is 32 because both children collapse to it: the indicator is 16 with 8px margins, and the
     * label's own 8px padding is pulled back by `margin: -2px 0`. Two pixels short of the gate.
     * The tiles whose label wraps to two lines already measured 62; this floor lifts the rest.
     *
     * Phone-only, like every other touch target here: 42px is the chip proportion frame 289:152
     * draws, and the desktop tiles keep it.
     */
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  /**
   * THE WHOLE TILE HAS TO BE THE TARGET. Styling the root as a card made the rest of it look
   * clickable and do nothing, which is the one behaviour a tile promises. The label stretches to
   * fill the width the indicator does not; the height is the stretched input's job, above.
   */
  roleTileLabel: { flexGrow: 1 },
  /**
   * Chosen: the brand tint the frames use for a selected surface, not a colour invented here.
   *
   * The whole `border` rather than `borderColor`: Griffel types the four-side shorthands as
   * `undefined`, so `borderColor` is a compile error, while `border` is supported outright.
   */
  roleTileChosen: {
    border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  // The row that adds one interviewer: the picker, then the one button that attaches a real id.
  adder: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-end', flexWrap: 'wrap' },
  /**
   * The picker keeps a floor of its own so a chip beside it cannot squash it to an unreadable
   * stub: below that floor the row wraps and the chips take the next line instead.
   */
  adderControl: { flex: '1 1 10rem', minWidth: '10rem' },
  /** The add box and the manage list both sit under the picker, full width of the column. */
  listEditor: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0, marginTop: tokens.spacingVerticalXS },
  listEditorRow: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-end', flexWrap: 'wrap', minWidth: 0 },
  /** One configured name per line, its removal on the right of it. */
  listRow: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', justifyContent: 'space-between', minWidth: 0 },
  /** Chips never grow; they take what is left and wrap within themselves. */
  adderChips: { flex: '0 1 auto', minWidth: 0 },
  /**
   * The "Other" box, BELOW the three-column Line rather than inside its third column.
   *
   * Frame `289:152` draws Interview as three equal columns and #333 pinned that shape; growing the
   * third one to hold a second control would reopen it. The chips already sit under the row for the
   * same reason, so this follows them.
   */
  otherSource: { maxWidth: '24rem' },
});

export interface NewCandidateFormProps {
  readonly onCreated?: (person: Person) => void;
  /** The frame puts Cancel in the footer beside Add candidate, not in the panel's head. */
  readonly onCancel?: () => void;
}

/** The frame's row: a 15rem label column carrying the heading and its description, then the fields. */
function Section({ title, hint, children }: { readonly title: string; readonly hint: string; readonly children: React.ReactNode }) {
  const styles = useStyles();
  return (
    <section className={styles.section} aria-label={title}>
      <div className={styles.labelColumn}>
        <h3 className={styles.h}>{title}</h3>
        <Caption1>{hint}</Caption1>
      </div>
      <div className={styles.sectionFields}>{children}</div>
    </section>
  );
}

/**
 * Add candidate (#258, frame 06b `289:152`).
 *
 * Role and tag choices are read from the catalog, never listed here. That is decision 3's
 * "roles must be configurable" made real: adding a profile is a data change, and this form
 * picks it up without an edit.
 *
 * INTERVIEWERS COME FROM AGENCY SETTINGS (#341), so Paul adds one without a deploy.
 *
 * THERE WAS AN "ADD ME" BUTTON HERE, and it is gone (#346). Julio asked for people rather than free
 * text so an interview could be linked to an Outlook account later, and with no staff directory the
 * only way to attach a real id was the signed-in user saying "that was me". But the button recorded
 * WHOEVER PRESSED IT — so when the coordinator filling this form was not the person who did the
 * interview, which is the normal case here, it wrote down the wrong name, labelled with their email
 * rather than their name. It also has no counterpart in frame 289:378. The id it captured is
 * written and read by nothing: the Outlook linking it existed for does not exist. A field whose
 * whole purpose is being right about who interviewed is better empty than confidently wrong, so
 * interviewers are chosen from the configured list, and a real id arrives when a directory does.
 *
 * WHERE THEY FOUND US is a closed list of five (#341): Indeed, LinkedIn, Instagram, Facebook,
 * Other. Stored as ids, because counting a source is the reason it stopped being a text box.
 */
export function NewCandidateForm({ onCreated, onCancel }: NewCandidateFormProps) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const options = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, staleTime: Infinity });
  const configuredInterviewers = useQuery({ queryKey: ['agency-interviewers'], queryFn: fetchAgencyInterviewers, staleTime: Infinity });

  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [interviewedOn, setInterviewedOn] = useState('');
  const [interviewers, setInterviewers] = useState<readonly Interviewer[]>([]);
  const [foundUsThrough, setFoundUsThrough] = useState('');
  const [foundUsThroughOther, setFoundUsThroughOther] = useState('');
  const [profileKey, setProfileKey] = useState('');
  const [tags, setTags] = useState<readonly string[]>([]);

  /**
   * MANAGING THE PICK LIST, from the screen that uses it (Paul, 2026-09-29; #372 part 4).
   *
   * Two states rather than one dialog: adding a name is a sentence HR types, and taking one off is
   * a decision about somebody already there. Both are closed by default, so the field looks exactly
   * as frame 289:378 draws it until HR asks for either.
   */
  const [addingInterviewer, setAddingInterviewer] = useState(false);
  const [newInterviewerName, setNewInterviewerName] = useState('');
  const [managingInterviewers, setManagingInterviewers] = useState(false);

  const submissionKey = useRef(crypto.randomUUID());
  const create = useMutation({
    // One key per submission, kept across retries, so a retry cannot create a twin (journey A).
    mutationFn: (input: Parameters<typeof createPerson>[0]) => createPerson(input, { idempotencyKey: submissionKey.current }),
    onSuccess: async (person) => {
      // The next candidate is a new submission, so it gets a new key.
      submissionKey.current = crypto.randomUUID();
      await queryClient.invalidateQueries({ queryKey: ['people'] });
      setGivenName('');
      setFamilyName('');
      setPreferredName('');
      setEmail('');
      setPhone('');
      setInterviewedOn('');
      setInterviewers([]);
      setFoundUsThrough('');
      setFoundUsThroughOther('');
      setProfileKey('');
      setTags([]);
      onCreated?.(person);
    },
  });

  const MAX_INTERVIEWERS = 10; // `candidateIntakeSchema` refuses an eleventh; say so here instead.

  /**
   * Same person twice is a slip, not an instruction — and it must dedupe whichever order they
   * arrive in, KEEPING THE ENTRY THAT CARRIES AN ID.
   *
   * Matching only on the incoming id once let the same person through twice, stored once with an id
   * and once without. The first fix dropped the duplicate but kept the FIRST entry, so the same flow
   * then stored them once WITHOUT the id — the same loss, on a field whose whole point is the id,
   * and order-dependent. An id arriving for a name already on the list upgrades that entry in place.
   * Kept after "Add me" was removed (#346): nothing carries an id today, but the rule is about which
   * entry wins when one does, and a directory will make that live again.
   */
  const addInterviewer = (person: Interviewer) => {
    const name = person.name.trim();
    if (name === '') return;
    setInterviewers((current) => {
      const byId = person.userId !== null ? current.findIndex((i) => i.userId === person.userId) : -1;
      if (byId !== -1) return current;
      const byName = current.findIndex((i) => i.name === name);
      if (byName !== -1) {
        // Upgrade in place when the incoming entry knows the id and the stored one does not.
        if (person.userId === null || current[byName]?.userId !== null) return current;
        return current.map((i, at) => (at === byName ? { userId: person.userId, name } : i));
      }
      if (current.length >= MAX_INTERVIEWERS) return current;
      return [...current, { userId: person.userId, name }];
    });
  };

  /**
   * ADDING TO AND REMOVING FROM THE AGENCY'S LIST — not this interview's answer.
   *
   * These are the two controls #372 part 4 asks for, and they write agency settings: the name
   * becomes available to everybody on the next candidate too, which is the point of the list
   * living in settings rather than in this component (#341).
   *
   * Both replace the cached list with what the SERVER returns rather than patching the local copy,
   * so two HR users adding names at the same time converge instead of each keeping their own idea
   * of the list. The mutation's own `operationId` makes a double-click one name, not two.
   */
  const addOption = useMutation({
    mutationFn: (label: string) => changeAgencyInterviewers({ operationId: crypto.randomUUID(), action: 'add', label }),
    onSuccess: (list) => {
      queryClient.setQueryData(['agency-interviewers'], list);
      setNewInterviewerName('');
      setAddingInterviewer(false);
    },
  });

  /**
   * REMOVING A NAME DOES NOT TOUCH ANY INTERVIEW THAT NAMES THEM, which is Paul's requirement and
   * the reason this is safe to offer here at all. An interview stores its interviewers as
   * `{ userId, name }` snapshots on the person, and both readers — the History timeline and the
   * candidate's intake card — render the stored name. Nothing resolves a past interview through
   * this list, so a name taken off it disappears from the dropdown and from nowhere else.
   */
  const removeOption = useMutation({
    mutationFn: (id: string) => changeAgencyInterviewers({ operationId: crypto.randomUUID(), action: 'remove', id }),
    onSuccess: (list) => queryClient.setQueryData(['agency-interviewers'], list),
  });

  /**
   * Picking from the dropdown ADDS TO THE LIST; it does not become the answer.
   *
   * An interview has more than one interviewer in it, which is why the chips exist at all (#258),
   * so the control is a way of naming the next one rather than a single-value field. The select
   * returns to its placeholder afterwards so the same person cannot look "already chosen" while
   * the chip beside it says the same thing twice.
   */
  const pickInterviewer = (id: string) => {
    // The one non-name entry in the dropdown, intercepted before any lookup: "+ Add" is a control
    // wearing an option's clothes, because a native <select> cannot hold a button.
    if (id === ADD_INTERVIEWER) {
      setAddingInterviewer(true);
      return;
    }
    const option = (configuredInterviewers.data?.interviewers ?? []).find((row) => row.id === id);
    if (option === undefined) return;
    addInterviewer({ userId: option.userId, name: option.label });
  };

  /**
   * "Other" with nothing typed is an answer that says nothing, and the write schema refuses it
   * (`writableCandidateIntakeSchema`). Refused here too, so HR sees a disabled button next to the
   * empty box rather than a 400 after filling the whole form.
   */
  const otherSourceMissing = foundUsThrough === 'other' && foundUsThroughOther.trim() === '';

  function submit(event: FormEvent) {
    event.preventDefault();
    const intake = {
      interviewedOn: interviewedOn === '' ? null : interviewedOn,
      interviewers: [...interviewers],
      foundUsThrough: foundUsThrough === '' ? null : foundUsThrough,
      /**
       * ONLY "Other" CARRIES TEXT, and this is the one place that decides it.
       *
       * The box is not emptied when the source changes, deliberately: it is hidden, so nothing
       * stale is on screen, and a reader who flips to LinkedIn and back has not lost the sentence
       * they typed. What gets SENT is settled here instead, which is also what the write schema
       * checks (`writableCandidateIntakeSchema`) — a named source beside a leftover sentence is a
       * contradiction, and a 400.
       */
      foundUsThroughOther: foundUsThrough === 'other' && foundUsThroughOther.trim() !== '' ? foundUsThroughOther.trim() : null,
    };
    const anyIntake = intake.interviewedOn !== null || intake.interviewers.length > 0 || intake.foundUsThrough !== null;
    create.mutate({
      name: { givenName: givenName.trim(), familyName: familyName.trim(), preferredName: preferredName.trim() },
      contact: { email, phone: phone.trim() === '' ? null : phone.trim() },
      jobProfileKeys: profileKey === '' ? [] : [profileKey],
      tags: [...tags],
      attributes: {},
      hireDate: null,
      // Omitted entirely when HR filled none of it, rather than sending three empties.
      ...(anyIntake ? { intake } : {}),
    });
  }

  const error = create.error as (Error & Partial<ApiError>) | null;

  return (
    <form className={styles.form} onSubmit={submit}>
      <Section title="Name" hint="As it appears on their ID. Preferred name is what we call them.">
        <div className={styles.pair}>
          <Field label="First name" required>
            <Input value={givenName} onChange={(_, d) => setGivenName(d.value)} />
          </Field>
          <Field label="Last name" required>
            <Input value={familyName} onChange={(_, d) => setFamilyName(d.value)} />
          </Field>
          <Field label="Preferred name">
            {/* The frame carries "Optional" as placeholder text inside the input, not as a
                caption below it — a hint here also made this field taller than its neighbours. */}
            <Input
              placeholder="Optional"
              value={preferredName}
              onChange={(_, d) => setPreferredName(d.value)}
            />
          </Field>
        </div>
      </Section>

      <Section title="Contact" hint="Their personal email gets the portal link. Field staff never get a Vigeo account.">
        <div className={styles.pair}>
          <Field label="Personal email" required>
            <Input type="email" value={email} onChange={(_, d) => setEmail(d.value)} />
          </Field>
          <Field label="Mobile phone">
            <Input type="tel" value={phone} onChange={(_, d) => setPhone(d.value)} />
          </Field>
          {/*
            * Frame 289:152 draws Contact as THREE columns — Personal email, Mobile phone, and an
            * empty 1px-tall spacer (node 289:359) — so these two fields keep the same width as the
            * name row above them. Without it they stretch to halves and render ~52% wider than the
            * frame draws them.
            *
            * The frame uses this device deliberately rather than everywhere: `Row — Dates`
            * (294:825/294:837) genuinely is halves. So this is the design being specific about
            * Contact, not a blanket rule, and the same spacer appears at 294:818 and 301:1092.
            */}
          <div aria-hidden="true" />
        </div>
      </Section>

      <Section title="Interview" hint="The record starts after the interview. This goes into their History.">
        {/* One Line of three, as frame 289:152 draws it: Interview date, Interviewed by, Found us
            through (nodes 289:366, 289:378, 289:389). Equal columns that WRAP — `flex: 1 1 12rem`,
            not the `1 0 0` this note used to claim: a zero basis makes `flexWrap` dead code and the
            three inputs overlapped at 375px. See the note on `pair` itself.

            Both pickers are what the frame draws (node 289:378 is a dropdown) and what #341 settled:
            the interviewers come from agency settings so Paul can add one without a deploy, and the
            sources are a closed list of five so they can be counted. The column keeps the frame's
            position and width, and the chosen names sit under the row. */}
        <div className={styles.pair}>
          <Field label="Interview date">
            <Input type="date" value={interviewedOn} onChange={(_, d) => setInterviewedOn(d.value)} />
          </Field>

          <Field label="Interviewed by">
            {/*
              THE CHOSEN NAMES SIT BESIDE THE CONTROL THEY ANSWER, in this one flex row.
              `adder` wraps, so at a normal width a chip reading "Khalia Horne" sits immediately
              right of "Choose a name", and on a narrow column it drops under the dropdown instead
              of squashing it. `alignItems: 'flex-end'` puts the chip on the dropdown's baseline.

              Two earlier placements were wrong. The first rendered the chips as a sibling of the
              whole three-column row, which left-aligned them under INTERVIEW DATE — a chip
              appeared to belong to a date box two columns away. The second (#361) moved them into
              this Field but kept them on the line below, where the answer still read as stranded
              under a lone narrow dropdown once "Add me" was removed (#346).

              This is deliberately NOT the same control as "Found us through" beside it. That one
              holds ONE value, so the closed dropdown can display the answer itself. This one holds
              up to MAX_INTERVIEWERS, which a <select> cannot show: the dropdown names the NEXT
              interviewer and the chips are the answer, each carrying its own removal. Frame
              289:378 draws a single dropdown and the handoff says nothing about more than one
              interviewer — the multi-value shape is `candidateIntakeSchema`'s, not the design's.
            */}
            <div className={styles.adder}>
              <Select
                className={styles.adderControl}
                // No aria-label: it would override the visible "Interviewed by" label and announce
                // something the user cannot see (WCAG 2.5.3).
                disabled={interviewers.length >= MAX_INTERVIEWERS}
                // Always back to the placeholder: this control names the NEXT interviewer, it does
                // not hold the answer. The chips beside it are the answer.
                value=""
                onChange={(_, d) => pickInterviewer(d.value)}
              >
                <option value="">Choose a name</option>
                {(configuredInterviewers.data?.interviewers ?? []).map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
                {/* Last, and separated by its wording: a control, not a colleague. */}
                <option value={ADD_INTERVIEWER}>{interviewerListCopy.add}</option>
              </Select>
              {interviewers.length === 0 ? null : (
                <TagGroup
                  aria-label="Interviewers"
                  className={styles.adderChips}
                  onDismiss={(_, d) => setInterviewers((current) => current.filter((_i, index) => String(index) !== d.value))}
                >
                  {interviewers.map((person, index) => (
                    <Tag key={`${person.userId ?? 'name'}:${person.name}`} value={String(index)} dismissible dismissIcon={{ 'aria-label': `Remove ${person.name}` }}>
                      {person.name}
                    </Tag>
                  ))}
                </TagGroup>
              )}
            </div>
            {interviewers.length < MAX_INTERVIEWERS ? null : (
              <Caption1>That is as many interviewers as a record holds.</Caption1>
            )}

            {/*
              MANAGING THE AGENCY'S LIST, under the control it feeds (#372 part 4).

              DELIBERATELY NOT THE CHIPS. A chip says "this person interviewed this candidate" and
              dismissing one takes them out of THIS interview; these two controls change who may be
              picked, for every candidate after this one. Paul's "removing a name doesn't erase it
              from past interviews" is exactly that distinction, and putting a "remove from list" on
              a chip would have collapsed the two meanings into one button.
            */}
            {!addingInterviewer ? null : (
              <div className={styles.listEditor}>
                <div className={styles.listEditorRow}>
                  <Field label={interviewerListCopy.newName} className={styles.adderControl}>
                    <Input
                      autoFocus
                      value={newInterviewerName}
                      onChange={(_, d) => setNewInterviewerName(d.value)}
                      // Enter saves, because typing a name and reaching for the mouse is the slow
                      // path HR is in the middle of.
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter') return;
                        event.preventDefault();
                        if (newInterviewerName.trim() !== '') addOption.mutate(newInterviewerName.trim());
                      }}
                    />
                  </Field>
                  <Button
                    appearance="primary"
                    // Trimmed, because the schema trims: a name of spaces would be refused by the
                    // server after the round trip, which is a worse way to learn the same thing.
                    disabled={newInterviewerName.trim() === '' || addOption.isPending}
                    onClick={() => addOption.mutate(newInterviewerName.trim())}
                  >
                    {addOption.isPending ? <Spinner size="tiny" /> : interviewerListCopy.save}
                  </Button>
                  <Button
                    disabled={addOption.isPending}
                    onClick={() => { setAddingInterviewer(false); setNewInterviewerName(''); addOption.reset(); }}
                  >
                    {interviewerListCopy.cancel}
                  </Button>
                </div>
                {addOption.error === null ? null : (
                  <Caption1 role="alert">{addOption.error.message}</Caption1>
                )}
              </div>
            )}

            <div className={styles.listEditor}>
              <Button
                appearance="transparent"
                onClick={() => setManagingInterviewers((open) => !open)}
                aria-expanded={managingInterviewers}
              >
                {managingInterviewers ? interviewerListCopy.done : interviewerListCopy.manage}
              </Button>
              {!managingInterviewers ? null : (
                <>
                  <Caption1>{interviewerListCopy.removalIsNotDeletion}</Caption1>
                  {(configuredInterviewers.data?.interviewers ?? []).length === 0 ? (
                    <Caption1>{interviewerListCopy.empty}</Caption1>
                  ) : (
                    (configuredInterviewers.data?.interviewers ?? []).map((option) => (
                      <div key={option.id} className={styles.listRow}>
                        <Caption1>{option.label}</Caption1>
                        <Button
                          size="small"
                          disabled={removeOption.isPending}
                          onClick={() => removeOption.mutate(option.id)}
                        >
                          {interviewerListCopy.remove(option.label)}
                        </Button>
                      </div>
                    ))
                  )}
                  {removeOption.error === null ? null : (
                    <Caption1 role="alert">{removeOption.error.message}</Caption1>
                  )}
                </>
              )}
            </div>
          </Field>

          <Field label="Found us through">
            <Select
              // Same reason as above: the visible label is the accessible name.
              value={foundUsThrough}
              onChange={(_, d) => setFoundUsThrough(d.value)}
            >
              <option value="">Not recorded</option>
              {candidateSourceSchema.options.map((source) => (
                <option key={source} value={source}>{candidateSourceLabels[source]}</option>
              ))}
            </Select>
          </Field>
        </div>

        {foundUsThrough !== 'other' ? null : (
          <Field
            className={styles.otherSource}
            label="Where did they find us?"
            required
            validationState={otherSourceMissing ? 'warning' : 'none'}
            validationMessage={otherSourceMissing ? 'Say where, so "Other" means something in a count.' : undefined}
          >
            <Input value={foundUsThroughOther} onChange={(_, d) => setFoundUsThroughOther(d.value)} />
          </Field>
        )}

        {configuredInterviewers.isError ? (
          // Said "you can still add yourself" until #346 removed the button that did that. A
          // silently empty dropdown looks like an agency with no interviewers, so it still says why.
          <Caption1>The interviewer list could not be loaded, so no names are offered. The rest of the form still works.</Caption1>
        ) : null}

      </Section>

      <Section title="Role" hint="Decides which checklist they get. Pick one.">
        {options.isError ? (
          // Without roles there is nothing to pick, and the button below stays disabled. Say so:
          // a permanently dead button with no explanation is worse than the failure itself.
          <MessageBar intent="error">
            <MessageBarBody>The roles could not be loaded, so a candidate cannot be added yet. Reload the page to try again.</MessageBarBody>
          </MessageBar>
        ) : options.isPending ? (
          <Spinner size="tiny" label="Loading roles" />
        ) : (
          <RadioGroup aria-label="Role" value={profileKey} onChange={(_, d) => setProfileKey(d.value)} className={styles.roleChips}>
            {(options.data?.jobProfiles ?? []).map((profile) => (
              // The size is the consequence of the choice, and a wrong role is expensive to undo
              // once a checklist exists. It is the count that role's checklist actually produces.
              <Radio
                key={profile.key}
                value={profile.key}
                label={{ className: styles.roleTileLabel, children: profile.label }}
                // mergeClasses, NOT a template literal. Griffel class strings each carry a sequence
                // hash; concatenating two of them makes Fluent's own mergeClasses discard the second
                // set, so the chosen tile rendered identically to an unchosen one. The repo's test
                // output said so ("multiple identifiers of atomic classes") and nothing failed.
                className={mergeClasses(styles.roleTile, profile.key === profileKey && styles.roleTileChosen)}
              />
            ))}
          </RadioGroup>
        )}
        {/*
          THE NOTE IS DELIBERATELY ABSENT UNTIL IT CAN BE TRUE.
          
          Frame 289:152 shows one line under the chips for the chosen role, and it counts PORTAL
          STEPS: "Home Health Aide (HHA): 22 portal steps for them to complete. You'll see each one,
          and approve it, on their Review tab." Design confirmed (2026-09-25) that the catalog's
          requirement count must NOT be used for it — one portal step can cover several
          requirements, so `jobProfiles[].items` (76 for an RN) is a different quantity, not a
          rounding of the same one.
          
          WHY IT IS BLANK, corrected: the data IS reachable. `catalog/index.ts` re-exports
          `stepDefinitionSeeds` and `isOnboardingStepBacked` from the same barrel that defines
          `catalogOptions`, so a per-role count needs no Cosmos and no server round-trip — an
          earlier comment here said otherwise and was wrong.
          
          The real obstacle is that it is not clear WHICH number the sentence means, and the
          candidates disagree by a factor of three: the onboarding step-backed requirements for a
          role (33 for HHA), or the ones a candidate could actually be issued today (9, because only
          26 step definitions are seeded against 47 step-backed requirements). Neither is the
          frame's 22. Rendering nothing until design says which, because a confident wrong number
          here is undetectable by a reader — which is how the first one survived.
        */}
      </Section>

      <Section title="Also applies" hint="Tags add items without changing the role. A contracted nurse is still a nurse.">
        <div className={styles.options}>
          {(options.data?.tags ?? []).map((tag) => (
            <Checkbox
              key={tag.key}
              label={tag.label}
              checked={tags.includes(tag.key)}
              onChange={(_, d) =>
                setTags((current) =>
                  d.checked === true ? [...current, tag.key] : current.filter((t) => t !== tag.key),
                )
              }
            />
          ))}
        </div>
      </Section>

      {error === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>
            {error.message}
            {(error.issues ?? []).length > 0 ? ` — ${(error.issues ?? []).join('; ')}` : ''}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.actions}>
        <Caption1>Next: prepare their offer.</Caption1>
        <div className={styles.actionButtons}>
          {create.isPending ? <Spinner size="tiny" label="Saving" /> : null}
          {onCancel === undefined ? null : (
            <Button appearance="secondary" type="button" onClick={onCancel} disabled={create.isPending}>
              Cancel
            </Button>
          )}
          {/* The frame says "Pick one", and a candidate with no role gets no checklist — the
              thing this form exists to produce. Refused here rather than at activation. */}
          <Button appearance="primary" type="submit" disabled={create.isPending || profileKey === '' || otherSourceMissing}>
            Add candidate
          </Button>
        </div>
      </div>
    </form>
  );
}
